/**
 * Pure helpers for the Design Library page — status semantics, the presigned-POST
 * form builder, the shared band-upload flow, and the review-lightbox backgrounds.
 * Kept separate from the component so they're unit-tested without a DOM.
 */
import { DESIGN_STYLE_CODE_RE } from '@fsp/contracts';
import type { ApiClient } from '@/lib/api/client';
import type { components } from '@/lib/api/types.gen';

export type DesignSummary = components['schemas']['DesignSummary'];
export type DesignFileView = DesignSummary['adult'];
export type DesignStatus = DesignFileView['status'];
export type DesignBand = 'adult' | 'youth';

// ── Style code (the short SKU-style code: fronts FR/FW/LC, backs LT-001) ──

/**
 * Normalize keystrokes for a style-code input to uppercase + trimmed (the code is always uppercase,
 * mirroring the Shopify SKU) so the operator can't accidentally save a lowercase / padded value.
 */
export function normalizeStyleCode(raw: string): string {
  return raw.trim().toUpperCase();
}

/**
 * Client-side mirror of the `@fsp/contracts` style-code shape (the Zod schema is authoritative). An
 * empty string is "no code" — valid; a non-empty code must match the shape.
 */
export function isValidStyleCode(code: string): boolean {
  return code === '' || DESIGN_STYLE_CODE_RE.test(code);
}

/**
 * True when a design matches the library search box — a case-insensitive substring over BOTH the name
 * AND the style code, so a design is findable by either. An empty query matches everything.
 */
export function matchesDesignSearch(
  d: Pick<DesignSummary, 'name' | 'styleCode'>,
  query: string,
): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return d.name.toLowerCase().includes(q) || (d.styleCode ?? '').toLowerCase().includes(q);
}

export type DesignSortMode = 'style' | 'name';

/**
 * Sort the client-loaded design library. FLAGGED designs float to the top in every mode (so the designer
 * spots a reported problem immediately). Then, within each group: `name` = A→Z; `style` = coded designs
 * first in code order (FR, FW, LC, LT-001 …), uncoded after by name — so the codes group together.
 * Pure + does not mutate the input.
 */
export function sortDesigns(
  designs: readonly DesignSummary[],
  mode: DesignSortMode,
): DesignSummary[] {
  const byName = (a: DesignSummary, b: DesignSummary) =>
    a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
  const byMode = (a: DesignSummary, b: DesignSummary): number => {
    if (mode === 'name') return byName(a, b);
    const ac = a.styleCode ?? '';
    const bc = b.styleCode ?? '';
    if (ac && bc) return ac.localeCompare(bc) || byName(a, b);
    if (ac) return -1;
    if (bc) return 1;
    return byName(a, b);
  };
  return [...designs].sort((a, b) => {
    const af = a.problem ? 0 : 1;
    const bf = b.problem ? 0 : 1;
    return af - bf || byMode(a, b); // flagged first, then the chosen mode
  });
}

/**
 * For DISPLAY, remove a leading/trailing style-code token from a design NAME so the code isn't shown
 * twice next to its badge/code line. This matters because a batch label + the scan tile show a FROZEN
 * snapshot of the name (captured at batch build, before the code was cleaned out of the name) alongside
 * the LIVE style code — so a name like "LT-045 Ford Nation" would render "LT-045 … LT-045 Ford Nation".
 * Boundary-aware (won't touch "LT-0455") + case-insensitive; never empties the name — a name that IS
 * just the code is returned unchanged (the caller decides whether to show it). Search still uses the raw
 * name, so this only affects what's shown, not what's findable.
 */
export function stripStyleCode(name: string, styleCode: string | null | undefined): string {
  const code = (styleCode ?? '').trim();
  const trimmed = name.trim();
  if (!code || !trimmed) return trimmed;
  const CODE = code.toUpperCase();
  const upper = trimmed.toUpperCase();
  const isSep = (ch: string | undefined) => ch !== undefined && /[\s:._/|–—-]/.test(ch);
  if (upper.startsWith(CODE)) {
    const rest = trimmed.slice(code.length);
    if (rest !== '' && isSep(rest[0])) {
      const after = rest.replace(/^[\s:._/|–—-]+/, '');
      if (after) return after;
    }
  }
  if (upper.endsWith(CODE)) {
    const head = trimmed.slice(0, trimmed.length - code.length);
    if (isSep(head[head.length - 1])) {
      const before = head.replace(/[\s:._/|–—-]+$/, '');
      if (before) return before;
    }
  }
  return trimmed;
}

export type DesignStatusMeta = { label: string; className: string; attention: boolean };

/** Colour is meaning: green = validated + printable, amber = still validating, red = rejected. */
export const DESIGN_STATUS_META: Record<DesignStatus, DesignStatusMeta> = {
  READY: {
    label: 'Ready',
    className:
      'border-emerald-300 bg-emerald-50 text-emerald-700 dark:border-emerald-900 dark:bg-emerald-950 dark:text-emerald-300',
    attention: false,
  },
  PENDING: {
    label: 'Validating…',
    className:
      'border-amber-300 bg-amber-50 text-amber-700 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-300',
    attention: false,
  },
  INVALID: {
    label: 'Rejected',
    className: 'border-destructive/40 bg-destructive/10 text-destructive',
    attention: true,
  },
};

/**
 * Stop polling a design that has sat PENDING longer than this — validation runs
 * within seconds of upload, so a still-PENDING row this old almost certainly means
 * the S3 upload failed (an orphan). Bounds the refetch so a stuck row can't drive
 * an unbounded 4s poll while the tab is focused.
 */
export const PENDING_POLL_WINDOW_MS = 3 * 60_000;

/**
 * True while a RECENTLY-created design is still validating — drives the polling
 * refetch. `nowMs` injectable for tests. Old PENDING rows (failed uploads) don't
 * keep the poll alive; the operator can Refresh manually.
 */
export function hasRecentPending(designs: readonly DesignSummary[], nowMs: number): boolean {
  return designs.some(
    (d) =>
      (d.adult.status === 'PENDING' || d.youth?.status === 'PENDING') &&
      nowMs - new Date(d.createdAt).getTime() < PENDING_POLL_WINDOW_MS,
  );
}

function round1(n: number): number {
  return Math.round(n * 10) / 10;
}

/**
 * Compact size + youth hint suffix for a design row in the mapping picker, e.g. " · 12″×16″ · +youth"
 * (empty when the adult dims aren't validated yet). Kept here (pure) so the picker + any list share it.
 */
export function designSizeHint(d: DesignSummary): string {
  const w = d.adult.printWidthIn;
  const h = d.adult.printHeightIn;
  const size = w != null && h != null ? ` · ${round1(w)}″×${round1(h)}″` : '';
  const youth = d.youth?.status === 'READY' ? ' · +youth' : '';
  return `${size}${youth}`;
}

/** Derived print size label for a band file, e.g. "12″ × 16″", or null when dims aren't validated yet. */
export function printSizeLabel(f: DesignFileView): string | null {
  const w = f.printWidthIn;
  const h = f.printHeightIn;
  if (w == null || h == null) return null;
  const round1 = (n: number) => Math.round(n * 10) / 10;
  return `${round1(w)}″ × ${round1(h)}″`;
}

/**
 * Build the multipart form for a presigned S3 POST: every policy field first, the
 * file LAST (S3 requires `file` to be the final field). Returns a FormData ready to
 * POST to the presigned `url`.
 */
export function buildUploadFormData(fields: Record<string, string>, file: File): FormData {
  const form = new FormData();
  for (const [k, v] of Object.entries(fields)) form.append(k, v);
  form.append('file', file);
  return form;
}

/**
 * Byte-progress callback for an in-flight upload. `fraction` is 0‥1 (loaded / total),
 * called repeatedly during the S3 POST and once with `1` on completion.
 */
export type UploadProgressFn = (fraction: number) => void;

/** Thrown when an upload is aborted via its `AbortSignal` — a user action, not a failure. */
export class UploadCancelledError extends Error {
  constructor() {
    super('Upload cancelled.');
    this.name = 'UploadCancelledError';
  }
}

/**
 * The browser→S3 upload seam. Default is {@link xhrUploadTransport}. Injectable so route
 * tests can drive progress + failures without a real XMLHttpRequest or network.
 */
export type UploadTransport = (params: {
  url: string;
  form: FormData;
  onProgress?: UploadProgressFn;
  signal?: AbortSignal;
}) => Promise<void>;

/**
 * POST a presigned S3 form via XMLHttpRequest — the only browser API that reports upload
 * progress (`fetch` has none). Resolves on a 2xx (a presigned POST returns 204); rejects
 * with a readable message on an HTTP error or network failure, or {@link UploadCancelledError}
 * when `signal` aborts.
 */
export function xhrUploadTransport({
  url,
  form,
  onProgress,
  signal,
}: {
  url: string;
  form: FormData;
  onProgress?: UploadProgressFn;
  signal?: AbortSignal;
}): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    if (signal?.aborted) {
      reject(new UploadCancelledError());
      return;
    }
    const xhr = new XMLHttpRequest();
    xhr.open('POST', url);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && e.total > 0) onProgress?.(e.loaded / e.total);
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        onProgress?.(1); // ensure the bar lands on 100% even if the last progress event was < total
        resolve();
      } else {
        reject(new Error(`S3 rejected the upload (${xhr.status}) — check it's a PNG under 50 MB.`));
      }
    };
    xhr.onerror = () => reject(new Error('network error during the upload.'));
    xhr.onabort = () => reject(new UploadCancelledError());
    signal?.addEventListener('abort', () => xhr.abort(), { once: true });
    xhr.send(form);
  });
}

/** Options for {@link uploadBandFile}: progress reporting, cancellation, and a test transport seam. */
export type UploadBandOptions = {
  onProgress?: UploadProgressFn;
  signal?: AbortSignal;
  transport?: UploadTransport;
};

/**
 * Upload one band's PNG for an EXISTING design: mint a presigned S3 POST for the design + band, then
 * POST the file straight to S3. The validate + thumbnail workers pick it up from the S3 event. Shared
 * by the new-design flow and the add-youth / replace-band row actions. `onProgress` reports S3 byte
 * progress (0‥1); `signal` cancels an in-flight upload; `transport` is injectable for tests.
 */
export async function uploadBandFile(
  client: ApiClient,
  designId: string,
  band: DesignBand,
  file: File,
  { onProgress, signal, transport = xhrUploadTransport }: UploadBandOptions = {},
): Promise<void> {
  const urlRes = await client.POST('/designs/upload-url', { body: { designId, band } });
  if (urlRes.error || !urlRes.data?.ok) {
    throw new Error(urlRes.error?.error?.detail ?? `Could not get a ${band} upload URL.`);
  }
  const { url, fields } = urlRes.data.data;
  try {
    await transport({ url, form: buildUploadFormData(fields, file), onProgress, signal });
  } catch (err) {
    if (err instanceof UploadCancelledError) throw err; // surfaced as a neutral state, not a failure
    const detail = err instanceof Error ? err.message : 'the upload failed.';
    throw new Error(`${band} upload failed: ${detail}`);
  }
}

/**
 * Review-lightbox backgrounds. A design PNG is transparent, so it's invisible on white and
 * hard to read on the table's checker — black is the default for reviewing light/halftone art,
 * with white + checker toggles for dark art. The preview WebP keeps its alpha, so the background
 * is purely a CSS choice (no re-render of the asset).
 */
export const PREVIEW_BACKGROUNDS = {
  black: { label: 'Black', className: 'bg-neutral-950' },
  white: { label: 'White', className: 'bg-neutral-100' },
  checker: {
    label: 'Checker',
    className: 'bg-[length:20px_20px] bg-[repeating-conic-gradient(#d4d4d4_0_25%,#fafafa_0_50%)]',
  },
} as const;
export type PreviewBackground = keyof typeof PREVIEW_BACKGROUNDS;
